import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';
import { SessionService } from './session.service';
import {
  GrowthScoreInitPayload,
  GrowthScoreQueueEntry,
  GrowthScoreSdk
} from '../interfaces/interfaces';

declare global {
  
  interface Window { 
    growthscore: GrowthScoreSdk; 
  }

}

@Injectable({
  providedIn: 'root'
})
export class GrowthScoreService {
  private readonly apiService = inject(ApiService);
  private readonly sessionService = inject(SessionService);
  private scriptLoadPromise: Promise<void> | null = null;

  private loadGrowthScoreScript(): Promise<void> {
    if (this.scriptLoadPromise) {
      return this.scriptLoadPromise;
    }

    this.scriptLoadPromise = new Promise((resolve, reject) => {
      if (!window.growthscore || !window.growthscore._q) {
        const queue: GrowthScoreQueueEntry[] = [];
        window.growthscore = {
          _q: queue,
          _v: 1,
          init: (appKey, payload, callback) => { 
            queue.push({ method: 'init', args: [appKey, payload, callback] }); 
          },
          survey: () => {
            queue.push({ method: 'survey', args: [] });
          },
          widget: () => {
            queue.push({ method: 'widget', args: [] });
          },
          discardSurvey: () => {
            queue.push({ method: 'discardSurvey', args: [] });
          },
          submitSurvey: () => {
            queue.push({ method: 'submitSurvey', args: [] });
          },
          testimonials: () => {
            queue.push({ method: 'testimonials', args: [] });
          },
          renderTestimonials: () => {
            queue.push({ method: 'renderTestimonials', args: [] });
          },
          referralWidget: () => {
            queue.push({ method: 'referralWidget', args: [] });
          },
          getTerms: () => {
            queue.push({ method: 'getTerms', args: [] });
          },
          getFriendsList: () => {
            queue.push({ method: 'getFriendsList', args: [] });
          }
        };
      }

      if (document.getElementById('growthScoreWidget')){
        return resolve();
      } 

      const script = document.createElement('script');
      script.id = 'growthScoreWidget';
      script.src = 'https://d2ud32ftwr5wmp.cloudfront.net/gs/v2.3/build.min.js';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load GrowthScore script'));
      document.head.appendChild(script);
    });

    this.scriptLoadPromise.catch(() => {
      this.scriptLoadPromise = null;
    });

    return this.scriptLoadPromise;
  }

  private getAppKey(marketplaceUserId: number): Promise<string | null> {
    const body = {
      user_id: marketplaceUserId,
      marketplace_user_id: marketplaceUserId,
      user_type: 3
    };

    return firstValueFrom(this.apiService.post({ 
      url: 'growthscore/getKeys', body 
    }))
      .then((response) => {
        const keys = response?.data;
        return keys?.[0]?.app_key ?? null;
      })
      .catch(() => null);
  }

  /**
   * Loads the SDK, initializes it, and pops the survey 
   */
  async triggerSurvey(): Promise<void> {
    if (this.sessionService.isPlatformServer()) return;

    try {
      const appData = this.sessionService.get('appData');
      const loginData = appData ? appData.vendor_details : null;
      if (!loginData || Number(loginData.is_guest_account)){
        return;
      } 

      const config = this.sessionService.get('config');
      const marketplaceUserId = (config?.marketplace_user_id) || loginData.marketplace_user_id;
      if (!marketplaceUserId) return;

      const appKey = await this.getAppKey(marketplaceUserId);
      if (!appKey){
        return;
      } 

      await this.loadGrowthScoreScript();

      const payload: GrowthScoreInitPayload = {
        surveynow: true,
        emailid: loginData.email || '',
        firstname: loginData.first_name || '',
        lastname: loginData.last_name || loginData.first_name || '',
        storeid: String(marketplaceUserId)
      };
      
      if (loginData.phone_no) {
        payload.phone = loginData.phone_no;
      }

      window.growthscore.init(appKey, payload, (error: string) => {
        if (error) {
          console.error('[GrowthScore] init error:', error);
          return;
        }
        window.growthscore.survey();
      });
    } catch (error) {
      console.error('[GrowthScore] failed to trigger survey:', error);
    }
  }
}
